import { useDisclosure } from "hooks/use-disclosure";

import { NoVersionFound } from "containers/admin/clients/touchpoint/components/no-version-found/no-version-found";
import { SubmitFormProps } from "components/modals/version-edit-modal/version-edit-modal";
import TouchpointVersionTable from "containers/admin/clients/touchpoint/tab-components/version-history-tab/components/touchpoint-version-table/touchpoint-version-table";
import toast from "components/partials/toast/toast";
import LockingModal from "components/modals/locking-modal/locking-modal";

import { useCurrentCampaign } from "hooks/api/use-campaigns";
import { useCurrentClient } from "hooks/api/use-clients";
import { useCurrentTouchpoint } from "hooks/api/use-current-touchpoint";
import {
  useCreateTouchpointVersion,
  useInvalidateTouchpointVersions,
  useTouchpointVersions,
  type TouchpointVersionAttributes,
} from "hooks/api/use-touchpoint-versions";
import { useTouchpointLocking } from "contexts/touchpoint-locking-context";

import { getErrorMessage } from "utilities";

const VersionHistoryTab = ({
  setSelectedVersion,
}: {
  setSelectedVersion: (version: TouchpointVersionAttributes) => void;
}) => {
  const currentCampaign = useCurrentCampaign();
  const currentClient = useCurrentClient();
  const currentTouchpoint = useCurrentTouchpoint();
  const createFirstDraftModal = useDisclosure();
  const versionScope = {
    clientId: currentClient.id,
    campaignId: currentCampaign.id,
    touchpointId: currentTouchpoint.id,
  };
  const { data: versions } = useTouchpointVersions(versionScope);
  const invalidateTouchpointVersions = useInvalidateTouchpointVersions(versionScope);
  const createVersionMutation = useCreateTouchpointVersion(versionScope);
  const { isLockingModalOpen } = useTouchpointLocking();

  const createDraftSubmit = ({ description, applyClientTheme, fiscalYear }: SubmitFormProps) => {
    return createVersionMutation
      .mutateAsync({
        description,
        applyClientTheme: applyClientTheme!,
        fiscalYear,
      })
      .then(() => {
        toast.success({
          data: {
            title: "Created draft",
          },
        });
        invalidateTouchpointVersions();
      })
      .catch((err) => {
        toast.error({
          data: {
            title: "Failed to create draft",
            message: getErrorMessage(err?.response?.data),
          },
        });
        invalidateTouchpointVersions();
      });
  };

  const touchpointType = currentTouchpoint.type;

  if (!versions?.items.length) {
    return <NoVersionFound touchpointType={touchpointType} onCreateDraft={createDraftSubmit} />;
  }

  return (
    <>
      {isLockingModalOpen && <LockingModal />}
      <TouchpointVersionTable
        touchpointType={touchpointType}
        creativeName={currentTouchpoint.name}
        setSelectedVersion={setSelectedVersion}
      />
    </>
  );
};

export default VersionHistoryTab;
